import {
    B2cProductSearchPayload,
    B2cProductsSearchPayload,
    BusinessBrand,
    CartSimpleQuery,
    type StartupQuery
} from "@/generated/graphql/b2c";
import { type Services } from "./services";

export type PreloadedStartupPayload = StartupQuery["Startup"];
export type PreloadedStartupResponse = null | undefined | string | {
    startup: { Startup: PreloadedStartupPayload };
    originalMpn?: string | null;
    featuredProducts?: B2cProductSearchPayload[] | null;
    simpleCart?: CartSimpleQuery | null;
    brandsByCategory: null | BusinessBrand[];
    productsSearch: null | B2cProductsSearchPayload;
};

export class Preloaded {
    originalMpn: string | null = null;
    startup: null | PreloadedStartupPayload = null;
    featuredProducts: null | B2cProductSearchPayload[] = null;
    simpleCart: null | CartSimpleQuery = null;
    brandsByCategory: null | BusinessBrand[] = null;
    productsSearch: null | B2cProductsSearchPayload = null;
    loaded = false;
    invalidated = false;

    constructor(protected services: Services) {

    }

    setupResponse(data?: PreloadedStartupResponse) {
        if (this.isValueRejected()) {
            return;
        }

        this.loaded = true;
        this.startup = this.parseResponseValue(data);
        this.originalMpn = typeof data === "object" ? data?.originalMpn : null;
    }

    async load() {
        if (this.isValueRejected()) {
            return;
        }

        this.loaded = true;
        const preloadedStartup = this.getPreloadedData() as PreloadedStartupResponse;

        this.startup = this.parseResponseValue(preloadedStartup);
        this.originalMpn = typeof preloadedStartup === "object" ? preloadedStartup?.originalMpn : null;
        this.featuredProducts = typeof preloadedStartup === "object" ? preloadedStartup?.featuredProducts as B2cProductSearchPayload[] : null;
        this.simpleCart = typeof preloadedStartup === "object" ? preloadedStartup?.simpleCart as CartSimpleQuery : null;
        this.brandsByCategory = typeof preloadedStartup === "object" ? preloadedStartup?.brandsByCategory as BusinessBrand[] : null;
        this.productsSearch = typeof preloadedStartup === "object" ? preloadedStartup?.productsSearch as B2cProductsSearchPayload : null;
    }

    invalidate() {
        this.invalidated = true;
        this.loaded = false;
        this.startup = null;
        this.simpleCart = null;
        this.brandsByCategory = null;
        this.productsSearch = null;
    }

    protected getPreloadedData() {
        const element = this.findDataScript("preloaded-startup");
        const data = element ? this.scriptDataParse(element) : null;

        if (data) {
            return data;
        }
    }

    protected scriptDataParse(element: HTMLScriptElement) {
        let result = null as null | Record<string, unknown>;

        try {
            const content = element.textContent?.trim();
            const data = content
                ? JSON.parse(content)
                : null;
            if (data && typeof data === "object") {
                result = data;
            }
        } finally {
            return result;
        }
    }

    protected findDataScript(key: string) {
        const element = document.getElementById(key);
        if (element instanceof HTMLScriptElement) {
            return element;
        }
    }

    protected isValueRejected() {
        return this.loaded || this.invalidated;
    }

    protected parseResponseValue(data?: PreloadedStartupResponse) {
        if (!data || typeof data === 'string' || !data?.startup) {
            return null;
        }

        return data.startup.Startup;
    }
}
